import React, { useEffect, useMemo, useState } from 'react';
import { Col, Input, Label, Row } from 'reactstrap';
import { ColumnSpec } from '~types/columnSpec';

import Button from '~components/Button';
import Modal from '~components/Modal';

const HEADER_ID = 'column-picker-modal-header';

export interface ColumnPickerGroup {
  title: string;
  keys: string[];
}

interface ComponentProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /** The table's full specs=true column metadata. */
  columns: ColumnSpec[];
  /** Display grouping for the picker. A column key not listed in any group falls into a
   *  trailing "Other" group rather than being dropped, so a newly-registered column still
   *  shows up without a frontend change. */
  groups: ColumnPickerGroup[];
  /** The columns currently shown, used to seed the draft each time the modal opens. */
  visibleColumnIds: string[];
  /** Called once, with the full next visible-column selection, when Apply is clicked. */
  onApply: (visibleColumnIds: string[]) => void;
  dataCy?: string;
}

const OTHER_GROUP_TITLE = 'Other';

interface GroupedColumns {
  title: string;
  columns: ColumnSpec[];
}

const groupColumns = (columns: ColumnSpec[], groups: ColumnPickerGroup[]): GroupedColumns[] => {
  const columnsByKey = new Map(columns.map((column) => [column.key, column]));
  const groupedKeys = new Set(groups.flatMap((group) => group.keys));

  const namedGroups = groups
    .map((group) => ({
      title: group.title,
      columns: group.keys.map((key) => columnsByKey.get(key)).filter((column): column is ColumnSpec => Boolean(column)),
    }))
    .filter((group) => group.columns.length > 0);

  const otherColumns = columns.filter((column) => !groupedKeys.has(column.key));

  return otherColumns.length > 0 ? [...namedGroups, { title: OTHER_GROUP_TITLE, columns: otherColumns }] : namedGroups;
};

/**
 * A grouped, grid-of-checkboxes column picker for tables with too many columns to comfortably
 * offer in a dropdown. Changes are drafted locally and only committed via Apply (Cancel/close
 * discards them) — deliberately not instant-apply like the small-list dropdown picker, since
 * toggling many boxes shouldn't refetch the table on every click.
 */
const ColumnPickerModal: React.FC<ComponentProps> = ({
  isOpen,
  setIsOpen,
  columns,
  groups,
  visibleColumnIds,
  onApply,
  dataCy = 'column-picker-modal',
}) => {
  const [draftVisibleColumnIds, setDraftVisibleColumnIds] = useState<string[]>(visibleColumnIds);

  // Reseed the draft from the current (possibly optimistic) visible set each time the modal
  // opens, so a previous Cancel never leaks into the next open.
  useEffect(() => {
    if (isOpen) setDraftVisibleColumnIds(visibleColumnIds);
  }, [isOpen]);

  const hideableColumns = useMemo(() => columns.filter((column) => column.hideable), [columns]);
  // Every non-hideable column's key belongs in the draft alongside whichever hideable ones are
  // toggled on — it's already force-checked in the UI regardless, and the draft must otherwise
  // match the shape of `visibleColumnIds` (every visible spec, hideable or not) for the
  // reopen/override-clearing comparison in useColumnVisibility to ever resolve.
  const nonHideableKeys = useMemo(
    () => columns.filter((column) => !column.hideable).map((column) => column.key),
    [columns],
  );
  const groupedColumns = useMemo(() => groupColumns(columns, groups), [columns, groups]);
  // This component only ever sees the column specs it is handed, but a calling table may render
  // additional client-only columns that have no spec entry (for example the Claim Items
  // dashboard's Origin Link affordance). A bare "columns shown" would therefore be a claim this
  // component cannot honour, so the header wording below is scoped to "selectable columns" instead.
  const shownCount = columns.filter((column) => !column.hideable || draftVisibleColumnIds.includes(column.key)).length;

  const toggleColumn = (key: string, checked: boolean) => {
    setDraftVisibleColumnIds((current) =>
      checked ? [...current, key] : current.filter((visibleKey) => visibleKey !== key),
    );
  };

  const selectAll = () =>
    setDraftVisibleColumnIds([...nonHideableKeys, ...hideableColumns.map((column) => column.key)]);
  const resetToDefault = () =>
    setDraftVisibleColumnIds([
      ...nonHideableKeys,
      ...hideableColumns.filter((column) => column.defaultVisible).map((column) => column.key),
    ]);

  const handleApply = () => {
    onApply(draftVisibleColumnIds);
    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      size="lg"
      data-cy={dataCy}
      labelledBy={HEADER_ID}
      header={
        <div>
          <div id={HEADER_ID}>Choose columns</div>
          <div className="fw-normal small text-muted">
            {shownCount} of {columns.length} selectable columns shown
          </div>
        </div>
      }
      footer={
        <>
          <Button size="sm" color="secondary" onClick={() => setIsOpen(false)} dataCy={`${dataCy}-cancel-button`}>
            Cancel
          </Button>
          <Button size="sm" color="success" onClick={handleApply} dataCy={`${dataCy}-apply-button`}>
            Apply
          </Button>
        </>
      }
    >
      <div className="d-flex justify-content-end mb-3">
        <Button size="sm" color="link" onClick={selectAll} dataCy={`${dataCy}-select-all-button`}>
          Select all
        </Button>
        <Button size="sm" color="link" onClick={resetToDefault} dataCy={`${dataCy}-reset-button`}>
          Reset to default
        </Button>
      </div>
      {groupedColumns.map((group) => {
        const groupHeadingId = `${dataCy}-group-${group.title.toLowerCase().replace(/\s+/g, '-')}`;
        return (
          <div key={group.title} className="mb-3" role="group" aria-labelledby={groupHeadingId}>
            <div id={groupHeadingId} className="small fw-bold text-uppercase text-muted mb-2">
              {group.title}
            </div>
            <Row>
              {group.columns.map((column) => {
                const checked = !column.hideable || draftVisibleColumnIds.includes(column.key);
                const inputId = `${dataCy}-${column.key}`;
                return (
                  <Col sm={6} key={column.key} className="d-flex align-items-center mb-2">
                    <Input
                      type="checkbox"
                      name={column.key}
                      id={inputId}
                      checked={checked}
                      disabled={!column.hideable}
                      onChange={(event) => toggleColumn(column.key, event.target.checked)}
                      data-cy={`${column.key}-checkbox`}
                    />
                    <Label for={inputId} className="ms-2 mb-0">
                      {column.label}
                      {!column.hideable && <span className="text-muted small ms-1">(always shown)</span>}
                    </Label>
                  </Col>
                );
              })}
            </Row>
          </div>
        );
      })}
    </Modal>
  );
};

export default ColumnPickerModal;
